// src/data/applicationExhibit.js
//
// THE ROSTER EXHIBIT (requirements v4, line 86: "The applicant may attach a copy of the seniority roster as
// an exhibit - digitally, on the application"). Task list section 352.
//
// WHY IT GOES THROUGH THE CALLABLE, NOT A BROWSER UPLOAD. An applicant has no account - he is applying for
// one - so he cannot authenticate to Storage, and a rule that let an unauthenticated browser write into a
// bucket is not a rule. The file travels inside the App Check-gated submitApplication call, and the ADMIN
// SDK writes it. That also puts the type and size checks on the server, where they cannot be edited by the
// person sending the file.
//
// WHY IT IS OPTIONAL, AND STAYS OPTIONAL. Ruling 194 makes the roster cross-check the PLATFORM's job, not
// the applicant's. His exhibit helps an officer who has no roster on file yet; it is never a condition of
// applying, and an application without one is not an exception.
//
// PURE. The form and the callable both read these limits, so a file the browser accepts is a file the
// server accepts (directives section XI).

export const MAX_BYTES = 5 * 1024 * 1024;        // 5 MB: a scanned roster page, not a library

// Kept narrow on purpose. A roster arrives as a scan or a photograph; nothing here needs to open an office
// document format, and every format admitted is one more parser in front of an officer.
export const ALLOWED = Object.freeze({
  'application/pdf': '.pdf',
  'image/jpeg': '.jpg',
  'image/png': '.png',
});

export const ALLOWED_ACCEPT = Object.keys(ALLOWED).join(',');

// The name is never trusted: it is what a stranger typed on his own computer. It is reduced to a safe
// filename, and the extension comes from the TYPE the server validated, not from the name.
export function safeExhibitName(name, contentType) {
  const base = String(name || 'roster')
    .replace(/\.[A-Za-z0-9]+$/, '')
    .replace(/[^A-Za-z0-9._-]/g, '_')
    .slice(0, 60) || 'roster';
  return base + (ALLOWED[contentType] || '');
}

export function exhibitPath(applicationId, name, contentType) {
  return 'applications/' + String(applicationId) + '/' + safeExhibitName(name, contentType);
}

// `bytes` is the decoded length. The browser knows it from the File; the server computes it from the buffer
// it decoded, and never from a number the browser sent.
export function validateExhibit(exhibit) {
  const e = exhibit || null;
  if (!e) return { ok: true, absent: true };                       // optional, and absence is not a fault
  const type = String(e.contentType || '');
  const bytes = Number(e.bytes || 0);
  if (!ALLOWED[type]) {
    return { ok: false, absent: false, why: 'A roster exhibit is a PDF, a JPG or a PNG. That file is ' + (type || 'of an unknown type') + '.' };
  }
  if (!bytes || bytes <= 0) return { ok: false, absent: false, why: 'That file is empty.' };
  if (bytes > MAX_BYTES) {
    return { ok: false, absent: false, why: 'A roster exhibit is up to 5 MB. That file is ' + Math.ceil(bytes / (1024 * 1024)) + ' MB - send the page, not the book.' };
  }
  return { ok: true, absent: false };
}
